import { Link } from "react-router-dom";
import { CheckCircle2, MapPin, ClipboardList } from "lucide-react";
import { Section, Overline, Card, IconBadge } from "./Shared";
import { SERVICES } from "../lib/data";
import { regionLabel } from "../lib/regions";

const pickChallenge = (region, patterns, fallbackIndex) => {
  const found = region.challenges.find((c) => patterns.some((p) => c.toLowerCase().includes(p)));
  return found || region.challenges[fallbackIndex % region.challenges.length];
};

const SERVICE_ANGLE = {
  bathymetric: { patterns: ["sediment", "capacity", "storage", "dredg", "volume"], text: (r, c) => `In ${r.name}, depth and volume data is central to ${c.toLowerCase()}. Our contour maps, volume tables and stage-storage curves give owners, utilities and regulators a defensible current baseline.` },
  vegetation: { patterns: ["vegetation", "milfoil", "hydrilla", "salvinia", "weed", "elodea", "pondweed", "stonewort", "hyacinth"], text: (r, c) => `Across ${r.name}, ${c.toLowerCase()} is a recurring management issue. Biovolume mapping shows exactly where plant beds are, how dense they are and how they change after treatment, drawdown or harvesting.` },
  "bottom-hardness": { patterns: ["sediment", "muck", "dredg", "silt"], text: (r, c) => `Bottom-hardness and composition layers separate firm sand and rock from soft organic sediment. For ${r.name} projects dealing with ${c.toLowerCase()}, that map tells you where material has accumulated and where dredging or capping pays off.` },
  "fish-habitat": { patterns: ["habitat", "fish", "bass", "walleye", "trout", "salmon", "crappie"], text: (r, c) => `${r.name} fisheries managers, guides and lake associations use our habitat layers to plan stocking, structure placement and shoreline projects, particularly where ${c.toLowerCase()} is a priority.` },
  "side-scan": { patterns: ["structure", "habitat", "navigation", "dock", "dam"], text: (r, c) => `Side-scan imagery reveals submerged timber, rock piles, old roadbeds, debris and infrastructure on ${r.name} lakebeds — useful for ${c.toLowerCase()}, navigation safety and dam or intake inspections.` },
  gis: { patterns: ["plan", "permit", "association", "district", "compact", "accounting"], text: (r, c) => `Every ${r.name} survey ships as GIS-ready shapefiles, GeoTIFFs and PDFs that drop straight into county, utility or consultant workflows — including the documentation typically requested for ${c.toLowerCase()}.` },
  "survey-planning": { patterns: ["access", "remote", "season", "window", "drawdown"], text: (r, c) => `We plan ${r.name} surveys around the ${r.season} window, launch access and water levels, so the crew is on the water once and captures every layer in a single visit — even where ${c.toLowerCase()} complicates logistics.` },
};

export const RegionOverview = ({ region, details }) => {
  const kind = regionLabel(region);
  return (
    <Section className="pt-0">
      <div className="max-w-3xl" data-testid="region-overview">
        <Overline>About lake mapping in {region.name}</Overline>
        <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Bathymetric and aquatic mapping across {region.name}</h2>
        <p className="mt-6 text-lg text-slate-700 leading-relaxed">{details.landscape}</p>
        <p className="mt-4 text-lg text-slate-700 leading-relaxed">
          With {region.lakes}, {region.name} lake managers are dealing with {region.challenges.map((c) => c.toLowerCase()).join("; ")}. Each of
          these questions begins with an accurate, current map: how deep the water really is, how much volume remains, where sediment and
          vegetation have accumulated, and what the bottom is made of. Lake Monitor AI produces that map with survey-grade sonar and GPS,
          then turns it into contour charts, volume tables, biovolume layers and GIS files you can act on.
        </p>
        <p className="mt-4 text-lg text-slate-700 leading-relaxed">
          Surveys in {region.name} are typically scheduled {region.season}. Our crew arrives with its own boat, sonar and positioning equipment,
          covers the entire waterbody on a planned transect grid, and delivers finished maps and a written summary within two to three weeks.
          Deliverables are formatted to suit the documentation typically referenced by the {region.agency} and local authorities across the {kind}.
        </p>
      </div>
    </Section>
  );
};

export const RegionCities = ({ region, details }) => (
  <Section className="pt-0">
    <div className="rounded-3xl border border-cyan-200 bg-cyan-50 p-8 sm:p-10" data-testid="region-cities">
      <div className="flex flex-col lg:flex-row lg:items-center gap-8">
        <div className="lg:w-1/2">
          <Overline>Where we work in {region.name}</Overline>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-slate-900">Lake mapping near {details.cities[0]}, {details.cities[1]} and across {region.abbr}</h2>
          <p className="mt-4 text-base text-slate-700 leading-relaxed">
            We regularly survey lakes, ponds and reservoirs around {details.cities.slice(0, -1).join(", ")} and {details.cities[details.cities.length - 1]} — and we travel to
            any waterbody in {region.name}, from private farm ponds and HOA lakes to the largest reservoirs. Travel is quoted up front with no surprises.
          </p>
        </div>
        <ul className="lg:w-1/2 flex flex-wrap gap-2">
          {details.cities.map((c) => (
            <li key={c} className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300 bg-white px-4 py-2 text-base text-slate-800">
              <MapPin className="w-4 h-4 text-cyan-700" /> Lake mapping near {c}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </Section>
);

export const RegionProjects = ({ region, details }) => (
  <Section className="pt-0">
    <div className="max-w-2xl">
      <Overline>Typical projects</Overline>
      <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">What {region.name} clients hire us for</h2>
    </div>
    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="region-projects">
      {details.projects.map((p, i) => (
        <Card key={p} className="p-7">
          <div className="font-mono text-3xl font-bold text-cyan-600/40">0{i + 1}</div>
          <p className="mt-3 text-lg text-slate-800 font-medium leading-snug">{p}</p>
        </Card>
      ))}
    </div>
  </Section>
);

export const RegionServiceDetail = ({ region }) => (
  <Section className="pt-0">
    <div className="max-w-2xl">
      <Overline>Services in {region.name}</Overline>
      <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Every mapping service, delivered on-site in {region.abbr}</h2>
    </div>
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="region-services">
      {SERVICES.map((s, i) => {
        const angle = SERVICE_ANGLE[s.slug];
        const challenge = angle ? pickChallenge(region, angle.patterns, i) : region.challenges[i % region.challenges.length];
        return (
          <article key={s.slug} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:border-cyan-300 transition-colors" data-testid={`region-service-${s.slug}`}>
            <div className="flex items-center gap-3">
              <IconBadge icon={s.icon} className="!w-10 !h-10 [&>svg]:w-5 [&>svg]:h-5" />
              <h3 className="text-xl font-semibold text-slate-900">{s.title} in {region.name}</h3>
            </div>
            <p className="mt-4 text-base text-slate-700 leading-relaxed">{s.short}</p>
            {angle && <p className="mt-3 text-base text-slate-700 leading-relaxed">{angle.text(region, challenge)}</p>}
            <Link to={`/services#${s.slug}`} className="mt-4 inline-flex text-base font-semibold text-cyan-700 hover:text-cyan-900">Learn about {s.title.toLowerCase()} →</Link>
          </article>
        );
      })}
    </div>
  </Section>
);

const WATERBODY_TYPES = [
  "Private ponds and farm ponds", "HOA and community lakes", "Municipal water-supply reservoirs", "Irrigation and stock-water reservoirs",
  "Hydropower and flood-control reservoirs", "Golf course and resort lakes", "Stormwater and detention ponds", "Natural lakes and glacial basins",
  "Millponds and historic impoundments", "Quarry and pit lakes", "Fishing club and hunting-club lakes", "Park district and state-park lakes",
];

export const RegionWaterbodyTypes = ({ region }) => (
  <Section className="pt-0">
    <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 shadow-[0_4px_20px_rgba(15,23,42,0.04)]" data-testid="region-waterbody-types">
      <div className="flex items-center gap-3">
        <ClipboardList className="w-6 h-6 text-cyan-700" />
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Waterbody types we survey in {region.name}</h2>
      </div>
      <p className="mt-3 text-base text-slate-600 max-w-3xl">No lake is too small or too large. The same survey-grade workflow scales from a one-acre pond to a reservoir with hundreds of miles of shoreline anywhere in the {regionLabel(region)}.</p>
      <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {WATERBODY_TYPES.map((t) => (
          <li key={t} className="flex items-center gap-2.5 text-base text-slate-800"><CheckCircle2 className="w-5 h-5 text-cyan-600 shrink-0" />{t}</li>
        ))}
      </ul>
    </div>
  </Section>
);
